import React, { ChangeEvent, FC, Fragment, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Grid, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MoreHorzIcon from '@material-ui/icons/MoreHoriz';
import moment from 'moment';
import classNames from 'classnames';

import { ManualNotificationType, ManualNotificationsItemUpdateInput } from 'api';
import { DatePickers, ValidatedCheckbox } from 'components';

const useStyles = makeStyles(theme => ({
  labelRoot: {
    marginLeft: -theme.spacing(1),
  },
  label: {
    fontSize: 14,
  },
  bold: {
    fontWeight: theme.typography.fontWeightBold,
  },
  error: {
    color: theme.custom.palette.red,
  },
}));

const Schedule: FC = () => {
  const classes = useStyles();
  const formik = useFormikContext<ManualNotificationsItemUpdateInput>();
  const { t } = useTranslation();
  const [scheduledAnchorEl, setScheduledAnchorEl] = useState(null);

  const { errors, setFieldValue, values } = formik;
  const { notification_type, send_at } = values;

  const isScheduled = notification_type === ManualNotificationType.Scheduled;

  const labelClasses = {
    root: classes.labelRoot,
    label: classNames(classes.label, {
      [classes.bold]: notification_type === ManualNotificationType.Scheduled,
    }),
  };

  function getScheduledText() {
    if (!send_at) {
      return t('messages:scheduleForLater');
    }

    const timestamp = moment.unix(send_at).format('MMM D, h:mm A');
    const hasError = !!errors.send_at;
    const text = t('messages:scheduledFor', { timestamp });

    return <span className={hasError ? classes.error : ''}>{text}</span>;
  }

  function toggleScheduled(event: ChangeEvent<HTMLInputElement>) {
    if (isScheduled) {
      setFieldValue('send_at', null);
      setFieldValue('notification_type', ManualNotificationType.Now);
    } else {
      setScheduledAnchorEl(event.currentTarget);
    }
  }

  function handleScheduleEdit(event: MouseEvent<HTMLButtonElement>) {
    setScheduledAnchorEl(event.currentTarget);
  }

  function handleCheckedValue(notificationType: ManualNotificationType) {
    return notificationType === ManualNotificationType.Scheduled;
  }

  function handleSchedulerClose() {
    setScheduledAnchorEl(null);
  }

  function handleScheduledSave(time: number) {
    setFieldValue('send_at', time);
    setFieldValue('notification_type', ManualNotificationType.Scheduled);
    setScheduledAnchorEl(null);
  }

  return (
    <Fragment>
      <Grid container alignItems="center">
        <Grid item>
          <ValidatedCheckbox
            name="notification_type"
            label={getScheduledText()}
            labelClasses={labelClasses}
            checkedFn={handleCheckedValue}
            onChange={toggleScheduled}
          />
        </Grid>
        {isScheduled && (
          <Grid item>
            <IconButton onClick={handleScheduleEdit}>
              <MoreHorzIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
      <DatePickers.DateTime
        enableTime
        value={send_at}
        anchorEl={scheduledAnchorEl}
        onClose={handleSchedulerClose}
        onSave={time => handleScheduledSave(time)}
      />
    </Fragment>
  );
};

export default Schedule;
